import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import { useMetaTags } from '../../services/useMetaTags';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';
import '../../assets/styles/certification/list.scss';

function indexById(list) {
    if (!Array.isArray(list)) {
        return {};
    }

    return list.reduce((acc, item) => {
        if (item && typeof item.id !== 'undefined') {
            acc[item.id] = item;
        }

        return acc;
    }, {});
}

export default function CertificationsListPage() {
    const {t} = useTranslation();
    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmittingType, setIsSubmittingType] = useState('');
    const [notFound, setNotFound] = useState(false);

    useMetaTags({
        title: t('certifications.title'),
        description: t('app.description'),
        metas: [{name: 'keywords', content: t('app.keywords')}],
    });

    const fetchPageData = useCallback(() => {
        setIsLoading(true);
        setNotFound(false);

        fetch('/api/v1/certifications', {credentials: 'same-origin'})
            .then(async (response) => {
                const json = await response.json().catch(() => null);
                return {response, json};
            })
            .then(({response, json}) => {
                const {kind, redirectUrl} = parseRouteResponse(response, json);

                if (kind === 'not_found') {
                    setPayload(null);
                    setNotFound(true);
                    return;
                }

                if ((kind === 'unauthorized' || kind === 'redirect' || kind === 'error') && redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                if (kind === 'error') {
                    setPayload(null);
                    return;
                }

                if (json?.state === 'active') {
                    setPayload(json);
                    return;
                }

                setPayload(null);
            })
            .catch(() => {
                setPayload(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchPageData();
    }, [fetchPageData]);

    const dictionary = useMemo(() => {
        const dictionaries = payload?.dictionaries || {};

        return {
            types: indexById(dictionaries.types || []),
            categories: indexById(dictionaries.categories || []),
            statuses: indexById(dictionaries.statuses || []),
        };
    }, [payload]);

    const onCreateRequest = (type) => {
        if (!payload?.actions?.createUrl) {
            return;
        }

        setIsSubmittingType(String(type));

        fetch(payload.actions.createUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: new URLSearchParams({type: String(type)}).toString(),
        })
            .then((response) => response.json())
            .then((result) => {
                if (result?.url) {
                    window.location.href = result.url;
                }
            })
            .finally(() => {
                setIsSubmittingType('');
            });
    };

    const onDeleteRequest = (deleteUrl) => {
        if (!deleteUrl) {
            return;
        }

        fetch(deleteUrl, {
            method: 'DELETE',
            credentials: 'same-origin',
        }).then(() => {
            fetchPageData();
        });
    };

    if (isLoading) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (!payload) {
        return null;
    }

    const requestTypes = payload.requestTypes || {};
    const certifications = Array.isArray(payload.certifications) ? payload.certifications : [];
    const organizationsUrl = payload?.actions?.organizationsUrl || '/organizations';
    const notVerifiedCaption = t('certifications.notVerifiedCaption').replace('%url%', organizationsUrl);

    return (
        <section id="quiz" className="spa-certifications-list">
            <div className="container">
                <div className="certification-block">
                    <div className="certification-header">
                        <div>{t('certifications.header')}</div>
                        <div className="certification-button-block">
                            <button
                                type="button"
                                className="btn"
                                disabled={!payload.hasCommissionMembership || isSubmittingType !== ''}
                                onClick={() => onCreateRequest(requestTypes.certification)}>
                                {t('certifications.createRequest')}
                            </button>
                            <button
                                type="button"
                                className="btn"
                                disabled={!payload.hasCommissionMembership || isSubmittingType !== ''}
                                onClick={() => onCreateRequest(requestTypes.portfolio)}>
                                {t('certifications.createPortfolioRequest')}
                            </button>
                        </div>
                    </div>

                    {!payload.hasCommissionMembership ? (
                        <div className="certification-membership-warning" dangerouslySetInnerHTML={{__html: notVerifiedCaption}}></div>
                    ) : (
                        <div className="certification-membership-caption">
                            <span>{t('certifications.yourCommission')}</span> {payload.commissionName || ''}
                        </div>
                    )}

                    <div>
                        <table className="certification-list">
                            <thead>
                                <tr>
                                    <th>{t('certifications.tableColumns.status')}</th>
                                    <th>{t('certifications.tableColumns.type')}</th>
                                    <th>{t('certifications.tableColumns.category')}</th>
                                    <th>{t('certifications.tableColumns.date')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {certifications.map((certification) => {
                                    const statusLabelKey = dictionary.statuses[certification.status]?.name || '';
                                    const isPortfolioRequest = Number(certification.requestType) === Number(requestTypes.portfolio);
                                    const typeLabelKey = dictionary.types[certification.type]?.name || '';
                                    const categoryLabelKey = dictionary.categories[certification.qualificationCategory]?.name || '';

                                    return (
                                        <tr key={`certification-${certification.id}`}>
                                            <td>{statusLabelKey ? t(statusLabelKey) : ''}</td>
                                            <td>{isPortfolioRequest ? t('certifications.annuallyPortfolio') : (typeLabelKey ? t(typeLabelKey) : '')}</td>
                                            <td>{isPortfolioRequest ? '' : (categoryLabelKey ? t(categoryLabelKey) : '')}</td>
                                            <td>{certification.dateCreated}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="editButton"
                                                    onClick={() => {
                                                        window.location.href = certification.links?.edit || '/certifications';
                                                    }}>
                                                </button>
                                                {certification.isDraft && (
                                                    <button
                                                        type="button"
                                                        className="deleteButton"
                                                        onClick={() => onDeleteRequest(certification.links?.delete)}>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </section>
    );
}
